import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaSearch, FaTicketAlt, FaTrophy,
  FaTag, FaArrowRight, FaEye, FaClock, FaHourglassHalf, FaLock,
} from "react-icons/fa";
import { MdCampaign } from "react-icons/md";
import Navbar from "../components/Navbar";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://api.dreamtoday.in";
const tabs = ["all", "current", "upcoming", "completed"];

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

/* ═══════════════════════════════════════════════════
   PALETTE
═══════════════════════════════════════════════════ */
const G = {
  gold:        "#C9A84C",
  goldDeep:    "#A8863A",
  goldLight:   "#E8C96A",
  goldGlow:    "rgba(201,168,76,0.28)",
  cream:       "#FFFDF5",
  creamDeep:   "#FFF8E7",
  creamMid:    "#FFF1CC",
  creamBorder: "#F0D98C",
  text:        "#5C4A1E",
  textMid:     "#8A6E34",
  textLight:   "#B89A5A",
};

/* ═══════════════════════════════════════════════════
   COUNTDOWN ENGINE
═══════════════════════════════════════════════════ */
const calcTime = (target) => {
  const diff = new Date(target) - Date.now();
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, expired: true };
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
    expired: false,
  };
};

const useCountdown = (date) => {
  const [t, setT] = useState(() => calcTime(date));
  useEffect(() => {
    setT(calcTime(date));
    const id = setInterval(() => setT(calcTime(date)), 1000);
    return () => clearInterval(id);
  }, [date]);
  return t;
};

/* ═══════════════════════════════════════════════════
   SMART TIMER
   For completed campaigns renders a fixed-height
   "Campaign Ended" block so all cards stay the same
   height regardless of type.
═══════════════════════════════════════════════════ */
const SmartTimer = ({ registrationEndDate, endDate, type }) => {
  const regExpired = !registrationEndDate || new Date(registrationEndDate) <= new Date();
  const activeDate = regExpired ? endDate : registrationEndDate;
  const { d, h, m, s, expired } = useCountdown(activeDate);
  const isReg = !regExpired;
  const label = isReg ? "Registration closes in" : "Campaign ends in";
  const Icon  = isReg ? FaClock : FaHourglassHalf;
  const col   = isReg ? G.goldDeep : "#7C5C20";
  const bg    = isReg
    ? `linear-gradient(135deg,${G.goldLight}44,${G.gold}22)`
    : "rgba(200,155,30,0.12)";
  const units = d > 0
    ? [{ v: d, l: "Days" }, { v: h, l: "Hrs"  }, { v: m, l: "Min" }]
    : [{ v: h, l: "Hrs"  }, { v: m, l: "Min"  }, { v: s, l: "Sec" }];

  /* ── COMPLETED: same fixed height as a live timer block ── */
  if (type === "completed") {
    return (
      <div className="rounded-2xl flex flex-col items-center justify-center gap-1"
        style={{
          background: "#F5F5F5",
          border: "1px solid #E0E0E0",
          minHeight: 74,
          padding: "12px 16px",
        }}
      >
        <FaLock style={{ color: "#9E9E9E", fontSize: 14, marginBottom: 2 }} />
        <span className="text-xs font-black tracking-widest uppercase" style={{ color: "#757575" }}>
          Campaign Ended
        </span>
        <span className="text-[10px] font-medium" style={{ color: "#BDBDBD" }}>
          {formatDate(endDate)}
        </span>
      </div>
    );
  }

  /* ── LIVE / UPCOMING: expired state ── */
  if (expired)
    return (
      <div className="flex items-center justify-center gap-2 rounded-2xl"
        style={{ background: "#FFF0F0", border: "1px solid #FFCDD2", minHeight: 74 }}>
        <FaLock style={{ color: "#C0392B", fontSize: 10 }} />
        <span className="text-xs font-black tracking-wide" style={{ color: "#C0392B" }}>
          Campaign Ended
        </span>
      </div>
    );

  /* ── LIVE / UPCOMING: active countdown ── */
  return (
    <div className="rounded-2xl overflow-hidden"
      style={{
        background: isReg ? G.creamMid : "#FFF4D6",
        border: `1px solid ${isReg ? G.creamBorder : "#DDB840"}`,
        minHeight: 74,
      }}
    >
      <div className="flex items-center gap-1.5 px-3 pt-2 pb-1">
        <Icon style={{ color: col, fontSize: 9 }} />
        <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: col }}>
          {label}
        </span>
      </div>
      <div className="flex items-center justify-center gap-1.5 px-3 pb-3">
        {units.map(({ v, l }, i) => (
          <React.Fragment key={l}>
            <div className="flex flex-col items-center flex-1">
              <div className="w-full flex items-center justify-center rounded-xl py-1.5"
                style={{ background: bg, border: `1px solid ${G.creamBorder}`, minHeight: 34 }}>
                <span className="text-base font-black tabular-nums" style={{ color: col }}>
                  {String(v).padStart(2, "0")}
                </span>
              </div>
              <span className="text-[8px] font-bold mt-1 uppercase tracking-wider" style={{ color: G.textLight }}>
                {l}
              </span>
            </div>
            {i < units.length - 1 && (
              <span className="text-sm font-black mb-5 flex-shrink-0" style={{ color: G.goldLight }}>:</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   PRIZE PILL
═══════════════════════════════════════════════════ */
const PrizePill = ({ prize, pos }) => {
  const medals = ["🥇", "🥈", "🥉"];
  const medal  = medals[pos] || "🏅";
  return (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
      style={{ background: G.creamMid, color: G.goldDeep, border: `1px solid ${G.creamBorder}` }}>
      <span style={{ fontSize: 10 }}>{medal}</span>
      <span className="truncate" style={{ maxWidth: 120 }}>
        {prize.prizeName}
        {prize.prizeAmount > 0 ? ` — ₹${(prize.prizeAmount || 0).toLocaleString()}` : ""}
      </span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   STATUS BADGE
═══════════════════════════════════════════════════ */
const StatusBadge = ({ type }) => {
  const map = {
    current:   { dot: "#22C55E", text: "LIVE",     bg: "rgba(255,252,235,0.95)" },
    upcoming:  { dot: "#F59E0B", text: "UPCOMING", bg: "rgba(255,252,235,0.95)" },
    completed: { dot: "#EF4444", text: "ENDED",    bg: "rgba(255,240,240,0.95)" },
  };
  const s = map[type] || map.upcoming;
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
      style={{ background: s.bg, border: `1px solid ${G.creamBorder}`, backdropFilter: "blur(10px)" }}>
      <span className="rounded-full flex-shrink-0"
        style={{ width: 6, height: 6, background: s.dot,
          boxShadow: type === "current" ? `0 0 6px ${s.dot}` : "none" }} />
      <span className="text-[10px] font-black tracking-widest" style={{ color: G.text }}>
        {s.text}
      </span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   TICKETS PROGRESS BAR
   Shown on ALL campaign types including completed.
   When type === "completed" the bar is grey/muted
   with a "Sold Out / All tickets sold" label.
═══════════════════════════════════════════════════ */
const TicketsProgressBar = ({ total, sold, type }) => {
  const safeSold  = Math.min(sold ?? 0, total);
  const available = Math.max(total - safeSold, 0);
  const pctAvail  = total > 0 ? (available / total) * 100 : 0;

  /* completed campaigns: greyed-out full bar */
  const isEnded   = type === "completed";
  const barColor  = isEnded
    ? "linear-gradient(90deg,#9E9E9E,#BDBDBD)"
    : pctAvail > 50 ? "linear-gradient(90deg,#C9A84C,#E8C96A)"
    : pctAvail > 20 ? "linear-gradient(90deg,#D97706,#F59E0B)"
    : "linear-gradient(90deg,#B91C1C,#EF4444)";

  const trackBg   = isEnded ? "#E0E0E0" : G.creamBorder;
  const fillPct   = isEnded ? 100 : pctAvail;      /* show full grey bar for ended */
  const labelColor = isEnded ? "#9E9E9E"
    : pctAvail > 50 ? G.goldDeep
    : pctAvail > 20 ? "#92400E"
    : "#991B1B";

  return (
    <div className="flex flex-col gap-1.5">
      {/* label row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <FaTicketAlt style={{ color: isEnded ? "#9E9E9E" : G.goldDeep, fontSize: 8 }} />
          <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: isEnded ? "#9E9E9E" : G.textLight }}>
            {isEnded ? "Tickets Sold" : "Tickets Available"}
          </span>
        </div>
        <span className="text-[10px] font-black tabular-nums" style={{ color: labelColor }}>
          {(sold || 0).toLocaleString()}
          <span className="font-semibold text-[9px]" style={{ color: isEnded ? "#BDBDBD" : G.textLight }}>
            /{(total || 0).toLocaleString()}
          </span>
        </span>
      </div>

      {/* track */}
      <div className="relative w-full overflow-hidden"
        style={{ height: 7, borderRadius: 99, background: trackBg }}>
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${fillPct}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          style={{ position: "absolute", left: 0, top: 0, height: "100%", borderRadius: 99,
            background: barColor,
            boxShadow: isEnded ? "none" : "0 0 6px rgba(201,168,76,0.45)" }}
        />
        {/* shimmer — only for active campaigns */}
        {!isEnded && (
          <motion.div
            initial={{ x: "-100%" }}
            whileInView={{ x: "200%" }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: "easeInOut", delay: 0.4 }}
            style={{ position: "absolute", top: 0, left: 0, width: "40%", height: "100%",
              background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.55),transparent)",
              borderRadius: 99 }}
          />
        )}
      </div>

      
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   CAMPAIGN CARD
   Uses CSS grid rows (image / timer / prizes /
   progress / stats / cta) so every card in the
   same row shares the same cell heights.
═══════════════════════════════════════════════════ */
const CampaignCard = ({ campaign, index, onViewWinners }) => {
  const navigate = useNavigate();
  const [imgErr,  setImgErr]  = useState(false);
  const [hovered, setHovered] = useState(false);
  const hasImage = campaign.images?.[0] && !imgErr;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{
        y: -6,
        boxShadow: "0 22px 60px rgba(201,168,76,0.32), 0 4px 20px rgba(0,0,0,0.07)",
        transition: { duration: 0.25, ease: "easeOut" },
      }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={()   => setHovered(false)}
      /* h-full + flex-col makes every card fill its grid cell */
      className="relative rounded-3xl overflow-hidden flex flex-col h-full cursor-pointer"
      style={{
        background: `linear-gradient(165deg,${G.cream} 0%,${G.creamDeep} 60%,${G.creamMid} 100%)`,
        border: `1.5px solid ${hovered ? G.gold : G.creamBorder}`,
        boxShadow: `0 4px 24px rgba(201,168,76,0.12), 0 1px 6px rgba(0,0,0,0.04)`,
        transition: "border-color 0.28s ease, box-shadow 0.28s ease",
      }}
      onClick={() => campaign.type !== "completed" && navigate(`/campaigns/${campaign._id}`)}
    >
      {/* ── IMAGE  fixed 220px ── */}
      <div className="relative overflow-hidden flex-shrink-0" style={{ height: 220 }}>
        {hasImage ? (
          <motion.img
            src={`${BASE_URL}${campaign.images[0]}`}
            alt={campaign.title}
            onError={() => setImgErr(true)}
            animate={{ scale: hovered ? 1.08 : 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center"
            style={{ background: `linear-gradient(135deg,${G.creamMid},${G.creamBorder})` }}>
            <FaTrophy style={{ color: G.goldLight, fontSize: 46, opacity: 0.35, marginBottom: 8 }} />
            <span className="text-xs font-medium" style={{ color: G.textLight }}>No Image</span>
          </div>
        )}

        {/* gradient overlay */}
        <div className="absolute inset-0"
          style={{ background: "linear-gradient(to top,rgba(52,32,4,0.88) 0%,rgba(52,32,4,0.18) 46%,transparent 68%)" }} />

        {/* shimmer sweep */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          initial={{ x: "-100%", opacity: 0 }}
          animate={hovered ? { x: "150%", opacity: 1 } : { x: "-100%", opacity: 0 }}
          transition={{ duration: 0.65, ease: "easeInOut" }}
          style={{ background: "linear-gradient(105deg,transparent 30%,rgba(248,220,100,0.18) 50%,transparent 70%)" }}
        />

        {/* status badge */}
        <div className="absolute top-3.5 right-3.5">
          <StatusBadge type={campaign.type} />
        </div>

        {/* price pill */}
        <div className="absolute top-3.5 left-3.5 flex items-center gap-1.5 px-2.5 py-1 rounded-full"
          style={{ background: "rgba(255,252,235,0.95)", border: `1px solid ${G.creamBorder}`, backdropFilter: "blur(10px)" }}>
          <FaTag style={{ color: G.goldDeep, fontSize: 8 }} />
          <span className="text-[10px] font-black" style={{ color: G.goldDeep }}>₹{campaign.price}</span>
        </div>

        {/* title */}
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-3.5">
          <h3 className="font-extrabold leading-snug"
            style={{ color: "#FFF8E7", textShadow: "0 1px 12px rgba(0,0,0,0.6)", fontSize: "clamp(15px,2vw,18px)" }}>
            {campaign.title}
          </h3>
          {campaign.description && (
            <p className="text-xs mt-0.5 font-medium line-clamp-1"
              style={{ color: "rgba(255,248,231,0.68)" }}>
              {campaign.description}
            </p>
          )}
        </div>
      </div>

      {/* ── BODY  flex-1 so all bodies share remaining height ── */}
      <div className="flex flex-col flex-1 p-4 gap-3">

        {/* TIMER — same height slot for ALL types */}
        <SmartTimer
          registrationEndDate={campaign.registrationEndDate}
          endDate={campaign.endDate}
          type={campaign.type}
        />

        {/* PRIZES — fixed min-height keeps slot consistent */}
        <div style={{ minHeight: 48 }}>
          {campaign.prizes?.length > 0 ? (
            <>
              <p className="font-black uppercase tracking-widest mb-1.5"
                style={{ color: G.textLight, fontSize: 9 }}>
                Prizes
              </p>
              <div className="flex flex-wrap gap-1.5">
                {campaign.prizes.slice(0, 2).map((p, i) => (
                  <PrizePill key={p._id} prize={p} pos={i} />
                ))}
                {campaign.prizes.length > 2 && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold"
                    style={{ background: G.creamMid, color: G.textMid, border: `1px solid ${G.creamBorder}` }}>
                    +{campaign.prizes.length - 2} more
                  </span>
                )}
              </div>
            </>
          ) : (
            /* empty placeholder keeps the same gap */
            <div style={{ height: 48 }} />
          )}
        </div>

        {/* PROGRESS BAR — shown for ALL types */}
        <div style={{ borderTop: `1px solid ${G.creamBorder}`, paddingTop: 12 }}>
          <TicketsProgressBar
            total={campaign.noOfTickets}
            sold={campaign.remainingTickets}
            type={campaign.type}
          />
        </div>

        {/* STATS ROW */}
        <div className="flex items-end justify-between pt-3"
          style={{ borderTop: `1px solid ${G.creamBorder}` }}>
          <div>
            <p className="font-semibold uppercase tracking-wider mb-0.5"
              style={{ color: G.textLight, fontSize: 9 }}>Start Date</p>
            <p className="text-sm font-extrabold" style={{ color: G.text }}>
              {formatDate(campaign.startDate)}
            </p>
          </div>
          <div className="text-right">
            <p className="font-semibold uppercase tracking-wider mb-0.5"
              style={{ color: G.textLight, fontSize: 9 }}>Tickets</p>
            <p className="text-sm font-extrabold" style={{ color: G.text }}>
              {(campaign.noOfTickets || 0).toLocaleString()}
            </p>
          </div>
        </div>

        {/* CTA — pinned to bottom via mt-auto */}
        <div className="mt-auto">
          {campaign.type === "completed" ? (
            <motion.button
              whileTap={{ scale: 0.97 }}
              whileHover={{ scale: 1.02 }}
              onClick={(e) => { e.stopPropagation(); onViewWinners(campaign); }}
              className="w-full rounded-2xl font-extrabold text-sm tracking-wide
                flex items-center justify-center gap-2"
              style={{
                background: `linear-gradient(135deg,${G.goldLight} 0%,${G.gold} 50%,${G.goldDeep} 100%)`,
                color: "#3B2A0A",
                boxShadow: `0 4px 18px ${G.goldGlow}`,
                paddingTop: 13, paddingBottom: 13,
                border: "none", cursor: "pointer",
              }}
            >
              <FaTrophy style={{ fontSize: 11 }} />
              View Winners
            </motion.button>
          ) : (
            <motion.button
              whileTap={{ scale: 0.97 }}
              whileHover={{
                boxShadow: "0 8px 28px rgba(201,168,76,0.45)",
                transition: { duration: 0.2 },
              }}
              onClick={(e) => { e.stopPropagation(); navigate(`/campaigns/${campaign._id}`); }}
              className="w-full rounded-2xl font-extrabold text-sm tracking-wide
                flex items-center justify-center gap-2"
              style={{
                background: `linear-gradient(135deg,${G.goldLight} 0%,${G.gold} 50%,${G.goldDeep} 100%)`,
                color: "#3B2A0A",
                boxShadow: `0 4px 18px ${G.goldGlow}`,
                paddingTop: 13, paddingBottom: 13,
                border: "none", cursor: "pointer",
                transition: "box-shadow 0.28s ease",
              }}
            >
              <FaEye style={{ fontSize: 11 }} />
              View Campaign
              <FaArrowRight style={{ fontSize: 10 }} />
            </motion.button>
          )}
        </div>
      </div>

      {/* glow border bloom */}
      <motion.div
        className="absolute inset-0 rounded-3xl pointer-events-none"
        animate={hovered
          ? { opacity: 1, boxShadow: `inset 0 0 0 1.5px ${G.goldLight}` }
          : { opacity: 0, boxShadow: "inset 0 0 0 0px transparent" }
        }
        transition={{ duration: 0.28 }}
      />
    </motion.div>
  );
};

/* ── NO CAMPAIGNS EMPTY STATE ── */
const EmptyState = ({ search, activeTab }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex flex-col items-center justify-center py-24 px-4"
  >
    <div className="relative mb-6">
      <div className="w-28 h-28 rounded-full bg-gradient-to-br from-yellow-100 to-amber-200
        flex items-center justify-center shadow-lg border border-yellow-300">
        <MdCampaign className="text-yellow-500 text-5xl" />
      </div>
      <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-amber-400
        flex items-center justify-center shadow-md">
        <FaSearch className="text-white text-xs" />
      </div>
    </div>
    <h3 className="text-xl font-bold text-yellow-800 mb-2">
      {search ? "No results found" : `No ${activeTab === "all" ? "" : activeTab} campaigns yet`}
    </h3>
    <p className="text-gray-500 text-sm text-center max-w-xs">
      {search
        ? `We couldn't find any campaigns matching "${search}". Try a different keyword.`
        : "Check back soon — exciting new campaigns are on the way!"}
    </p>
  </motion.div>
);

/* ═══════════════════════════════════════════════════
   MAIN PAGE — all logic unchanged
═══════════════════════════════════════════════════ */
const AllCampaigns = () => {
  const [activeTab,        setActiveTab]        = useState("all");
  const [search,           setSearch]           = useState("");
  const [campaigns,        setCampaigns]        = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState("");
  const [showWinnerModal,  setShowWinnerModal]  = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${BASE_URL}/api/admin/getcampaignsbytype`)
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          const { upcoming = [], current = [], completed = [] } = d.campaigns;
          const tagged = [
            ...upcoming.map((c)  => ({ ...c, type: "upcoming"  })),
            ...current.map((c)   => ({ ...c, type: "current"   })),
            ...completed.map((c) => ({ ...c, type: "completed" })),
          ];
          setCampaigns(tagged);
        } else {
          setError(d.message || "Failed to load campaigns.");
        }
      })
      .catch(() => setError("Network error. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((c) => {
      const matchesTab    = activeTab === "all" || c.type === activeTab;
      const matchesSearch = c.title.toLowerCase().includes(search.toLowerCase());
      return matchesTab && matchesSearch;
    });
  }, [activeTab, search, campaigns]);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-b from-white via-[#FFF7DB] to-white pt-28 pb-20 px-4">

        {/* PAGE HEADER */}
        <div className="max-w-7xl mx-auto mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4
            bg-gradient-to-r from-yellow-600 via-yellow-700 to-yellow-800
            bg-clip-text text-transparent">
            Explore Campaigns
          </h1>
          <p className="text-gray-600">Discover luxury prize campaigns and join before they close</p>
        </div>

        {/* SEARCH BAR */}
        <div className="max-w-4xl mx-auto mb-10">
          <div className="flex items-center bg-white border border-yellow-300 rounded-2xl px-5 py-4 shadow-sm">
            <FaSearch className="text-yellow-700 mr-3 shrink-0" />
            <input
              type="text"
              placeholder="Search campaigns..."
              className="w-full outline-none text-gray-700"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button onClick={() => setSearch("")}
                className="ml-2 text-gray-400 hover:text-gray-600 text-lg leading-none">
                ×
              </button>
            )}
          </div>
        </div>

        {/* TABS */}
        <div className="flex justify-center gap-4 mb-14 flex-wrap">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2 rounded-full capitalize transition ${
                activeTab === tab
                  ? "bg-gradient-to-r from-yellow-600 to-yellow-700 text-white shadow-md"
                  : "bg-white border border-yellow-300 text-gray-700 hover:bg-yellow-50"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* LOADING */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin" />
            <p className="text-yellow-700 font-medium">Loading campaigns...</p>
          </div>
        )}

        {/* ERROR */}
        {!loading && error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-24"
          >
            <div className="w-20 h-20 rounded-full bg-red-50 border border-red-200
              flex items-center justify-center mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <p className="text-red-500 font-semibold text-lg mb-1">Something went wrong</p>
            <p className="text-gray-400 text-sm">{error}</p>
          </motion.div>
        )}

        {/* CAMPAIGNS GRID
            items-stretch ensures every card in a row
            stretches to the tallest sibling's height  */}
        {!loading && !error && filteredCampaigns.length > 0 && (
          <div className="max-w-7xl mx-auto grid gap-8 sm:grid-cols-2 lg:grid-cols-3 items-stretch">
            {filteredCampaigns.map((campaign, index) => (
              <CampaignCard
                key={campaign._id}
                campaign={campaign}
                index={index}
                onViewWinners={(c) => { setSelectedCampaign(c); setShowWinnerModal(true); }}
              />
            ))}
          </div>
        )}

        {/* EMPTY STATE */}
        {!loading && !error && filteredCampaigns.length === 0 && (
          <EmptyState search={search} activeTab={activeTab} />
        )}

      </div>

      {/* WINNER MODAL — unchanged */}
      {showWinnerModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-yellow-200"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-yellow-800 flex items-center gap-2">
                <FaTrophy className="text-yellow-600" />
                Winners
              </h2>
              <button
                onClick={() => setShowWinnerModal(false)}
                className="text-gray-400 hover:text-gray-700 text-xl"
              >
                ×
              </button>
            </div>

            {selectedCampaign.winners?.length > 0 ? (
              <div className="space-y-4 max-h-[400px] overflow-y-auto pr-1">
                {selectedCampaign.winners.map((winner) => (
                  <div key={winner._id}
                    className="p-4 rounded-xl border border-yellow-200 bg-gradient-to-r from-yellow-50 to-amber-50">
                    <div className="flex justify-between items-start">
                      <p className="font-bold text-gray-800">{winner.userName}</p>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                        #{winner.prizePosition}
                      </span>
                    </div>
                    <div className="mt-3 text-sm text-gray-600 space-y-1">
                      <p>🎁 <span className="font-medium">{winner.winningGift}</span></p>
                      <p>🎟 Ticket: <span className="font-semibold">{winner.ticketIds?.join(", ")}</span></p>
                      <p>
                        Status:{" "}
                        <span className={`font-semibold ${winner.status === "claimed" ? "text-green-600" : "text-yellow-600"}`}>
                          {winner.status}
                        </span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <FaTrophy className="text-yellow-300 text-4xl mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Winners will be announced soon.</p>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </>
  );
};

export default AllCampaigns;